// useClientStats — stats del cliente para la app móvil.
//
// FUENTE ÚNICA DE VERDAD: mismas reglas que Performance Lab (importa sus
// helpers exportados, NO reimplementa): RTDB /signals_history,
// telegram_sent===true, dimensión started_at, día calendario hora Chile,
// WR sobre WIN/LOSS (PUSH fuera del denominador).
import { useEffect, useMemo, useState } from 'react';
import { ref, query, orderByChild, equalTo, get } from 'firebase/database';
import { db } from '../../firebase';
import {
  santiagoDateStr, santiagoMidnightToUTCms, shiftDays, statsOfSubset,
} from '../../components/PerformanceLab';

const HANDBALL_RE = /handball|hbl|ehf|asobal|starligue|handbold|handboll/i;

export function sportOf(signal) {
  if (signal.sport) return String(signal.sport).toLowerCase();
  return HANDBALL_RE.test(String(signal.league || signal.liga || '')) ? 'handball' : 'basquet';
}

export default function useClientStats() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const q = query(ref(db, 'signals_history'), orderByChild('telegram_sent'), equalTo(true));
    get(q)
      .then((snap) => {
        if (!alive) return;
        const data = snap.val() || {};
        const list = Object.entries(data).map(([id, s]) => ({ id, ...s }));
        list.sort((a, b) => (b.started_at || 0) - (a.started_at || 0));
        setSignals(list);
        setLoading(false);
      })
      .catch((e) => { if (alive) { setError(e); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = santiagoDateStr(now);
    const todayStart = santiagoMidnightToUTCms(todayStr);
    const tomorrowStart = santiagoMidnightToUTCms(shiftDays(todayStr, 1));
    const monthStart = santiagoMidnightToUTCms(shiftDays(todayStr, -30));

    const inWin = (s, a, b) => (s.started_at || 0) >= a && (s.started_at || 0) < b;
    const hoy = signals.filter((s) => inWin(s, todayStart, tomorrowStart));
    const activasHoy = hoy.filter((s) => !['WIN', 'LOSS', 'PUSH'].includes(s.result));
    const mesSubset = signals.filter((s) => inWin(s, monthStart, tomorrowStart));
    const mes = statsOfSubset(mesSubset);

    // Unidades / yield del mes: +stake en WIN, −stake en LOSS (stake real
    // publicado, mismo campo que usa el mensaje de cierre de Telegram).
    let unidades = 0; let staked = 0;
    for (const s of mesSubset) {
      const st = parseFloat(s.stake_u_publicado || 1) || 1;
      if (s.result === 'WIN') { unidades += st; staked += st; }
      else if (s.result === 'LOSS') { unidades -= st; staked += st; }
    }
    const yieldPct = staked > 0 ? (unidades / staked) * 100 : null;

    // Últimos 10 días (incluye hoy): neto de unidades por día Chile.
    const days = [];
    for (let i = 9; i >= 0; i--) {
      const dStr = shiftDays(todayStr, -i);
      const a = santiagoMidnightToUTCms(dStr);
      const b = santiagoMidnightToUTCms(shiftDays(dStr, 1));
      const subset = signals.filter((s) => inWin(s, a, b));
      const st = statsOfSubset(subset);
      let net = 0;
      for (const s of subset) {
        const stk = parseFloat(s.stake_u_publicado || 1) || 1;
        if (s.result === 'WIN') net += stk; else if (s.result === 'LOSS') net -= stk;
      }
      days.push({
        dayStr: dStr, isToday: i === 0,
        label: i === 0 ? 'Hoy' : ['D', 'L', 'M', 'M', 'J', 'V', 'S'][new Date(`${dStr}T12:00:00Z`).getUTCDay()],
        decided: st.decided, wins: st.wins, net,
      });
    }
    const d10 = days.reduce((acc, d) => ({ wins: acc.wins + d.wins, decided: acc.decided + d.decided }), { wins: 0, decided: 0 });

    return { hoy, activasHoy, mes, unidades, yieldPct, days, racha10: d10 };
  }, [signals]);

  return { signals, loading, error, ...stats };
}
